import React, { useCallback, useEffect, useState } from 'react';
import {
  fetchSettings, updateSettings, fetchAiPricing, setAiPrice, resetAiPrice,
  fetchTenantLogo, uploadTenantLogo, deleteTenantLogo, fetchBlobObjectUrl,
} from '../services/api';
import { Button, Card, Chips, Loading, ErrorText, errorMessage } from '../components/ui';
import { formatRate } from '@shared/aiCost';
import { useMe } from '../hooks/useMe';

// Mirrors the mobile SettingsScreen: the same two tenant-wide lists, edited the
// same way. Each add/remove is its own operation against the server, which
// returns the updated settings - so what renders is always what was stored,
// never an optimistic guess.
// readOnly still SHOWS the list: a member needs to know which file types and
// tags exist in order to upload against them - they just cannot change the set.
function ListSection({ title, hint, items, onAdd, onRemove, placeholder, readOnly = false }) {
  const [value, setValue] = useState('');

  function submit() {
    const trimmed = value.trim();
    if (!trimmed) return;
    onAdd(trimmed);
    setValue('');
  }

  return (
    <Card title={title} hint={hint}>
      {items.length
        ? <Chips items={items.map((i) => ({ id: i, label: i }))} onRemove={readOnly ? undefined : onRemove} />
        : <p className="muted" style={{ marginTop: 0 }}>Nothing configured yet.</p>}
      {readOnly ? null : (
      <div className="row">
        <input
          className="input"
          style={{ flex: 1, minWidth: 180 }}
          placeholder={placeholder}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && submit()}
        />
        <Button compact onClick={submit} disabled={!value.trim()}>Add</Button>
      </div>
      )}
    </Card>
  );
}


// The rate table the cost figures are priced from. Editable because published
// AI prices change, and a stale rate makes every estimate on the Agents page
// quietly wrong. Rows a tenant has not touched follow the built-in figure, so
// a price change we ship still lands for everyone else.
function AiPricingSection({ readOnly = false }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState('');
  const [draft, setDraft] = useState({ input: '', output: '' });
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(async () => {
    setError('');
    try { setRows(await fetchAiPricing()); } catch (err) { setError(errorMessage(err)); }
  }, []);
  useEffect(() => { setLoading(true); load().finally(() => setLoading(false)); }, [load]);

  function startEdit(row) {
    setEditing(row.model);
    setDraft({ input: String(row.input), output: String(row.output) });
    setError('');
  }

  async function save(model) {
    const input = Number(draft.input);
    const output = Number(draft.output);
    if (!Number.isFinite(input) || !Number.isFinite(output) || input < 0 || output < 0) {
      setError('Rates must be numbers of zero or more.');
      return;
    }
    try {
      setRows(await setAiPrice({ model, input, output }));
      setEditing('');
    } catch (err) { setError(errorMessage(err)); }
  }

  async function reset(model) {
    try { setRows(await resetAiPrice(model)); setEditing(''); } catch (err) { setError(errorMessage(err)); }
  }

  // Most tenants care about the handful of models they actually use, and the
  // full table is long enough to bury everything below it.
  const edited = rows.filter((r) => r.overridden);
  const visible = showAll ? rows : (edited.length ? edited : rows.slice(0, 6));

  return (
    <Card
      id="ai-pricing"
      title="AI rates"
      hint="USD per 1M tokens, used to estimate the spend shown against each agent. Update these when a provider changes its published prices."
    >
      <ErrorText>{error}</ErrorText>
      {loading ? <Loading text="Loading rates…" rows={2} /> : (
        <>
          <div className="rate-list">
            {visible.map((row) => (
              <div key={row.model} className="rate-row">
                <div className="rate-model">
                  {row.model}
                  {row.overridden ? <span className="rate-badge">edited</span> : null}
                  {!row.builtIn ? <span className="rate-badge">added</span> : null}
                </div>
                {readOnly ? (
                  <span className="rate-value rate-value-static">{formatRate(row.input, row.output)}</span>
                ) : editing === row.model ? (
                  <div className="rate-edit">
                    <input className="input" inputMode="decimal" aria-label={`${row.model} input rate`}
                      value={draft.input} onChange={(e) => setDraft((d) => ({ ...d, input: e.target.value }))} />
                    <input className="input" inputMode="decimal" aria-label={`${row.model} output rate`}
                      value={draft.output} onChange={(e) => setDraft((d) => ({ ...d, output: e.target.value }))} />
                    <Button compact onClick={() => save(row.model)}>Save</Button>
                    {row.overridden ? <Button compact secondary onClick={() => reset(row.model)}>Reset</Button> : null}
                    <Button compact secondary onClick={() => setEditing('')}>Cancel</Button>
                  </div>
                ) : (
                  <button type="button" className="rate-value" onClick={() => startEdit(row)}>
                    {formatRate(row.input, row.output)}
                  </button>
                )}
              </div>
            ))}
          </div>
          {rows.length > visible.length || showAll ? (
            <button type="button" className="linklike" onClick={() => setShowAll((v) => !v)}>
              {showAll ? 'Show fewer' : `Show all ${rows.length} models`}
            </button>
          ) : null}
          <p className="card-hint" style={{ marginTop: 12 }}>
            Estimates only. Your provider's invoice is the authority — a model the table doesn't
            recognise is priced at a mid-range rate and marked as estimated.
          </p>
        </>
      )}
    </Card>
  );
}


// The logo drawn at the top of every generated report. Stored in the tenant's
// own container and loaded by the report engine at render time, so uploading
// one here is the only step - nothing else needs configuring.
//
// A tenant with no logo simply gets no logo in the header; there is no stand-in
// wordmark, which is why an empty state here means blank reports rather than
// someone else's branding.
function ReportLogoSection({ readOnly = false }) {
  const [status, setStatus] = useState(null);
  const [preview, setPreview] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const data = await fetchTenantLogo();
      setStatus(data);
      if (data?.hasLogo && data.blobName) {
        try { setPreview(await fetchBlobObjectUrl(data.blobName)); } catch { setPreview(''); }
      } else {
        setPreview('');
      }
    } catch (err) { setError(errorMessage(err)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function choose(event) {
    const file = event.target.files?.[0];
    // Clear immediately so picking the same file twice still fires onChange -
    // otherwise a failed upload cannot be retried without choosing another file.
    event.target.value = '';
    if (!file) return;
    setBusy(true);
    setError('');
    try {
      await uploadTenantLogo(file);
      await load();
    } catch (err) { setError(errorMessage(err)); } finally { setBusy(false); }
  }

  async function remove() {
    setBusy(true);
    setError('');
    try {
      await deleteTenantLogo();
      await load();
    } catch (err) { setError(errorMessage(err)); } finally { setBusy(false); }
  }

  return (
    <Card title="Report logo" hint="Shown at the top of every generated report. PNG or JPEG, up to 2MB.">
      <ErrorText>{error}</ErrorText>
      {preview
        ? <img src={preview} alt="Report logo" className="logo-preview" />
        : <p className="muted" style={{ marginTop: 0 }}>
            {status === null ? 'Checking…' : 'No logo set — reports are generated without one.'}
          </p>}
      {readOnly ? null : (
        <div className="row" style={{ marginTop: 10 }}>
          <label className="btn secondary compact" style={{ cursor: busy ? 'default' : 'pointer' }}>
            {busy ? 'Working…' : status?.hasLogo ? 'Replace logo' : 'Upload logo'}
            <input type="file" accept="image/png,image/jpeg" onChange={choose} disabled={busy} style={{ display: 'none' }} />
          </label>
          {status?.hasLogo ? <Button compact danger onClick={remove} disabled={busy}>Remove</Button> : null}
        </div>
      )}
    </Card>
  );
}

export default function SettingsPage() {
  // Settings are tenant-wide configuration. Members still SEE them - the file
  // types and tags are what they upload against - but the API only accepts
  // changes from an owner or admin.
  const { isAdmin } = useMe();
  const [settings, setSettings] = useState({ uploadTypes: [], documentTags: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const next = await fetchSettings();
      setSettings({ uploadTypes: next.uploadTypes || [], documentTags: next.documentTags || [] });
    } catch (err) {
      setError(errorMessage(err));
    }
  }, []);

  useEffect(() => { setLoading(true); load().finally(() => setLoading(false)); }, [load]);

  async function apply(operation, value) {
    setError('');
    try {
      const next = await updateSettings(operation, value);
      setSettings({ uploadTypes: next.uploadTypes || [], documentTags: next.documentTags || [] });
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  if (loading) return <Loading text="Loading settings…" rows={2} />;

  return (
    <>
      <h1>Settings</h1>
      <p className="sub">
        Shared across every project in your organisation.
        {isAdmin ? '' : ' Only an owner or admin can change these.'}
      </p>
      <ErrorText>{error}</ErrorText>

      <ListSection
        title="File types"
        hint="What kind of documents can be uploaded and tagged. An agent's upload category matches against this list."
        items={settings.uploadTypes}
        placeholder="e.g. Drawings"
        readOnly={!isAdmin}
        onAdd={(value) => apply('addUploadType', value)}
        onRemove={(value) => apply('removeUploadType', value)}
      />

      <ListSection
        title="Document tags"
        hint="Tags attachable to an upload. The backend rejects an upload carrying a tag that isn't on this list."
        items={settings.documentTags}
        placeholder="e.g. Record"
        readOnly={!isAdmin}
        onAdd={(value) => apply('addDocumentTag', value)}
        onRemove={(value) => apply('removeDocumentTag', value)}
      />

      <ReportLogoSection readOnly={!isAdmin} />

      <AiPricingSection readOnly={!isAdmin} />
    </>
  );
}
